/**
 * Núcleo do modo ao vivo: tipos e contas puras sobre os pedidos reais da Kiwify, e o contrato dos
 * módulos (`RealModule`) que o `withRealSales` compõe por cima da simulação.
 *
 * Cada assunto (mídia, VSL, ajustes, financeiro, plano, funil, backend, decisões, recuperação) vive
 * no próprio arquivo e devolve um `RealModule`; o `realSales.ts` só monta o contexto (`RealCtx`), faz
 * as vendas e pergunta aos módulos. Sem import circular: os módulos importam daqui, nunca do
 * `realSales.ts`.
 */
import type {
  ColumnarSeries,
  DashboardSnapshot,
  DataProvider,
  Freshness,
  KpiId,
  KpiIdV2,
  KpiValue,
  KpiValueV2,
  ListKind,
  ListQuery,
  LiveMessage,
  Order,
  OrderStatus,
  Page,
  PainelProviderV2,
  PaymentMethod,
  Query,
  Settings,
} from '@rota/contracts';
import { addMonthsKey } from '@/lib/monthKey';
import { channelGroup, splitOriginFilter } from '@/lib/salesChannel';
import { isOpenNow } from '@/lib/salesRules';
import { getWithRetry } from './http';
import type { MediaGap, RealMedia } from './realMedia';
import type { RealInsights } from './realTraffic';
import type { RealVsl } from './realVsl';

export const HOUR = 3_600_000;
export const DAY = 24 * HOUR;
export const BRT = 3 * HOUR;
export const CACHE_MS = 30_000;

/** Linha de pedido como o servidor manda (sem dado pessoal). */
export interface RealOrderRow {
  id: string;
  ref: string | null;
  /** Data da venda (aprovação; para quem não pagou, a criação): período, listas e séries usam esta. */
  at: number;
  /** Criação do pedido (Pix gerado, tentativa de cartão); ausente em servidor antigo (= `at`). */
  createdAt?: number;
  /** Aprovação do pagamento (null se nunca foi pago); ausente em servidor antigo. */
  approvedAt?: number | null;
  /** Data do reembolso ou chargeback; ausente em servidor antigo. */
  refundedAt?: number | null;
  /** Motivo da recusa como a Kiwify mandou (código ou texto); ausente quando não veio. */
  refusalReason?: string | null;
  /** Pix aguardando: quando vence; vencido ou trocado por outro pedido pago do mesmo produto. */
  pixExpiresAt?: number | null;
  pixExpired?: boolean;
  replaced?: boolean;
  status: string;
  /** Status como a Kiwify mandou ("abandoned" = carrinho abandonado); ausente em servidor antigo. */
  rawStatus?: string | null;
  /** Mesmo comprador em vários pedidos (pedaço do hash do e-mail); ausente em servidor antigo. */
  buyer?: string | null;
  product: string | null;
  kind: 'front' | 'upsell' | 'premium' | 'outro';
  grossCents: number;
  baseCents: number | null;
  commissionCents: number | null;
  feeCents: number | null;
  /** Parte dos coprodutores (divisão da Kiwify); null sem a divisão; ausente em servidor antigo. */
  coproductionCents?: number | null;
  installments: number | null;
  payment: string | null;
  channel: 'automacao' | 'ig_bio' | 'meta_ads' | 'outro' | 'sem_origem';
  utmSource: string | null;
  utmContent: string | null;
  /** UTMs brutas, src e sck como chegaram na venda; ausentes em servidor antigo. */
  utmMedium?: string | null;
  utmCampaign?: string | null;
  utmTerm?: string | null;
  src?: string | null;
  sck?: string | null;
  /** Atribuição gravada no pedido: 'contact', 'automation' ou 'other'; ausente em servidor antigo. */
  attribution?: string | null;
  firstTouch: string | null;
  lastTouch: string | null;
  conversation: boolean;
  /** O clique do anúncio (cookie _fbc) chegou na venda; ausente em servidor antigo. */
  clickTracked?: boolean;
  /** Venda de anúncio: campanha e anúncio pelo nome da Meta. */
  campaignName?: string | null;
  adName?: string | null;
  /** 1º aviso da Kiwify recebido pelo servidor (o atraso do webhook = isto − aprovação); ausente em servidor antigo. */
  receivedAt?: number | null;
  /** Compra enviada à Meta pela API de Conversões; null = CAPI nunca ligada; ausente em servidor antigo. */
  capiSent?: boolean | null;
}

export interface RealOrdersResult {
  orders: RealOrderRow[];
  /** Último aviso da Kiwify recebido pelo servidor (qualquer pedido). */
  lastEventAt: number | null;
  /** Reembolsos e chargebacks feitos no período (pela data do reembolso); ausente em servidor antigo. */
  refunds?: RealOrderRow[];
  /** 1º aviso do webhook da Kiwify: antes disso só há a exportação (sem abandonos nem todos os Pix). */
  webhookSince?: number | null;
  /** A lista ficou incompleta mesmo pedindo em pedaços: os números do período são parciais. */
  incomplete?: boolean;
  /**
   * 1º pedido da base inteira (exportação ou webhook): antes disso não há dado nenhum, e dia sem
   * dado não é "R$ 0". Ausente em servidor antigo.
   */
  dataStartMs?: number | null;
  /**
   * Silêncio da Kiwify que já é anormal (aviso), calculado no servidor com o ritmo real dos eventos
   * naquele horário. Ausente em servidor antigo (aí o painel calcula com os pedidos que tem).
   */
  silenceWarnMs?: number | null;
  /**
   * A consulta falhou e estes são os pedidos da última leitura boa (mesmo começo, até `staleSince`):
   * números reais, mas parados desde então. Ausente quando a leitura é de agora.
   */
  staleSince?: number;
}
export type FetchOrders = (from: number, to: number) => Promise<RealOrdersResult>;

/** Teto de pedidos por consulta no servidor (`painelSales.ts`, ORDERS_LIMIT). */
export const ORDERS_PAGE = 5000;

interface SalesResponse {
  orders: RealOrderRow[];
  lastEventAt: number | null;
  refunds?: RealOrderRow[];
  webhookSince?: number | null;
  truncated?: boolean;
  dataStartMs?: number | null;
  silenceWarnMs?: number | null;
}

/**
 * Pedidos do período sem corte: o servidor devolve no máximo `ORDERS_PAGE` pedidos por consulta, os
 * mais novos primeiro. Quando a lista vem cortada, guarda os completos e pede de novo só o que ficou
 * antes do mais velho recebido, até acabar. Se não der (pedidos demais no mesmo instante ou consultas
 * demais), o resultado volta marcado como incompleto e a tela avisa.
 */
export function makeFetchOrders(get: (url: string) => Promise<Response> = getWithRetry): FetchOrders {
  const one = async (from: number, to: number): Promise<SalesResponse> => {
    const r = await get(`/api/painel/vendas?from=${from}&to=${to}&limit=${ORDERS_PAGE}`);
    if (!r.ok) throw new Error(`vendas ${r.status}`);
    return (await r.json()) as SalesResponse;
  };
  return async (from, to) => {
    const orders: RealOrderRow[] = [];
    const refunds = new Map<string, RealOrderRow>();
    let hasRefunds = false;
    let lastEventAt: number | null = null;
    let webhookSince: number | null | undefined;
    let dataStartMs: number | null | undefined;
    let silenceWarnMs: number | null | undefined;
    let incomplete = true;
    let end = to;
    for (let page = 0; page < 40; page++) {
      const j = await one(from, end);
      if (j.lastEventAt != null) lastEventAt = Math.max(lastEventAt ?? 0, j.lastEventAt);
      if (j.webhookSince !== undefined)
        webhookSince =
          j.webhookSince == null || webhookSince == null
            ? (webhookSince ?? j.webhookSince)
            : Math.min(webhookSince, j.webhookSince);
      if (j.dataStartMs !== undefined)
        dataStartMs =
          j.dataStartMs == null || dataStartMs == null
            ? (dataStartMs ?? j.dataStartMs)
            : Math.min(dataStartMs, j.dataStartMs);
      if (j.silenceWarnMs !== undefined && silenceWarnMs === undefined) silenceWarnMs = j.silenceWarnMs;
      if (j.refunds) {
        hasRefunds = true;
        for (const o of j.refunds) refunds.set(o.id, o);
      }
      // Servidor antigo não diz se cortou: lista cheia = cortada.
      const cut = j.truncated ?? j.orders.length >= ORDERS_PAGE;
      if (!cut) {
        orders.push(...j.orders);
        incomplete = false;
        break;
      }
      // Os pedidos do instante mais velho podem ter ficado pela metade: entram na próxima consulta.
      const oldest = j.orders.reduce((m, o) => Math.min(m, o.at), Number.POSITIVE_INFINITY);
      const next = oldest + 1;
      if (!Number.isFinite(oldest) || next >= end) {
        orders.push(...j.orders);
        break;
      }
      orders.push(...j.orders.filter((o) => o.at > oldest));
      end = next;
    }
    return {
      orders,
      lastEventAt,
      ...(hasRefunds ? { refunds: [...refunds.values()] } : {}),
      ...(webhookSince !== undefined ? { webhookSince } : {}),
      ...(dataStartMs !== undefined ? { dataStartMs } : {}),
      ...(silenceWarnMs !== undefined ? { silenceWarnMs } : {}),
      ...(incomplete ? { incomplete: true } : {}),
    };
  };
}

export const fetchRealOrders: FetchOrders = makeFetchOrders();

// ---------- datas (São Paulo, UTC−3) ----------

export const dayStart = (key: string): number => Date.parse(`${key}T00:00:00Z`) + BRT;

/** Dia de hoje em São Paulo (AAAA-MM-DD). */
export const todayKey = (now: number): string => new Date(now - BRT).toISOString().slice(0, 10);

/** `to` para no agora (dados); `end` é o fim do último dia (eixo dos gráficos, com o futuro vazio). */
export function rangeMs(q: Query, now: number): { from: number; to: number; end: number } {
  const from = dayStart(q.range.from);
  const end = dayStart(q.range.to) + DAY;
  // "Agora" arredondado para o minuto: as telas que pedem o mesmo período no mesmo minuto dividem a
  // mesma consulta (o cache é por intervalo) em vez de cada uma ir ao servidor.
  const to = Math.min(end, Math.max(Math.ceil(now / 60_000) * 60_000, from + 1));
  return { from, to, end };
}

/**
 * Janela de comparação: `to` é o corte (o mesmo horário do período atual) e `end` o fim do último dia
 * dela (eixo dos gráficos). Sempre em dias inteiros de São Paulo: "período anterior" volta o mesmo
 * número de dias (7D de hoje até 22:14 contra os 7 dias antes, até 22:14 do último) e "mês passado"
 * volta para as mesmas datas do mês anterior (31/10 → 30/09; 31/03 → 28/02), nunca 30 dias fixos.
 */
export function compareMs(q: Query, now: number): { from: number; to: number; end: number } | null {
  const { from, to, end } = rangeMs(q, now);
  const back = (ms: number) => ({ from: from - ms, to: to - ms, end: end - ms });
  switch (q.compare) {
    case 'none':
      return null;
    case 'yesterday_same_time':
      return back(DAY);
    // A média de 4 semanas não existe ao vivo (o seletor nem oferece): cai na semana passada.
    case 'same_weekday':
    case 'avg_4w_same_weekday':
      return back(7 * DAY);
    case 'previous_month': {
      const cf = dayStart(addMonthsKey(q.range.from, -1));
      const tk = addMonthsKey(q.range.to, -1);
      const ce = dayStart(tk) + DAY;
      // Quanto do último dia já passou (até agora, em Hoje; o dia inteiro, num dia fechado).
      const ct = Math.min(ce, dayStart(tk) + (to - dayStart(q.range.to)));
      return { from: cf, to: Math.max(ct, cf + 1), end: Math.max(ce, cf + 1) };
    }
    default:
      return back(Math.max(1, Math.round((end - from) / DAY)) * DAY);
  }
}

// Regra única do "mês passado" (a mesma do calendário): reexportada para quem já importa daqui.
export { addMonthsKey };

/** Início do dia (São Paulo) do 1º pedido da base: antes disso, "sem dado" (null), nunca zero. */
export const coverageStart = (dataStartMs: number | null | undefined): number | null =>
  dataStartMs == null ? null : dayStart(new Date(dataStartMs - BRT).toISOString().slice(0, 10));

/** "14/10" do início da cobertura, para as notas. */
export const coverageLabel = (start: number): string => {
  const d = new Date(start - BRT).toISOString();
  return `${d.slice(8, 10)}/${d.slice(5, 7)}`;
};

export const bucketOf = (at: number, size: number): number => Math.floor((at - BRT) / size) * size + BRT;

/** Tamanho do balde dos minigráficos: hora num dia só; dia em períodos maiores. */
export const sparkSize = (r: { from: number; end: number }): number =>
  r.end - r.from <= DAY + HOUR ? HOUR : DAY;

// ---------- agregação ----------

export const isPaid = (o: RealOrderRow) => o.status === 'paid';
/**
 * Faturamento de um pedido: o valor do produto. Os juros do parcelamento pagos pelo comprador ficam com
 * a Kiwify e não são faturamento (dono, 28/10/2026); `grossCents` é o total cobrado.
 */
export const revenueOf = (o: Pick<RealOrderRow, 'baseCents' | 'grossCents'>): number =>
  o.baseCents ?? o.grossCents;

/** Reembolso ou chargeback (o servidor grava "chargedback"; aceita as duas grafias, como o servidor). */
export const isRefundLike = (status: string | null | undefined): boolean =>
  status === 'refunded' || status === 'chargedback' || status === 'chargeback';

/**
 * Venda aprovada no período: paga ou reembolsada depois. É o que entra no faturamento do dia da venda;
 * o reembolso sai uma vez só, na linha de reembolsos, pela data dele.
 */
export const isSold = (o: RealOrderRow): boolean => isPaid(o) || isRefundLike(o.status);

export function matches(o: RealOrderRow, q: Query): boolean {
  const f = q.filters;
  if (f.utmSources?.length) {
    // Canal da venda (o recorte do Hoje): a regra única do canal do servidor (`@/lib/salesChannel`).
    const { groups, sources } = splitOriginFilter(f.utmSources);
    if (groups && !groups.has(channelGroup(o.channel))) return false;
    // Venda de anúncio conta como "meta" mesmo quando o link usa {{site_source_name}} (utm_source=ig/fb):
    // o canal já vem resolvido pelo servidor.
    const src = o.channel === 'meta_ads' ? 'meta' : (o.utmSource ?? '').toLowerCase();
    if (sources.length && !sources.some((s) => s.toLowerCase() === src)) return false;
  }
  // Cada produto no próprio filtro: o Premium (consulta) nunca entra no "20X"; produto desconhecido
  // ("outro") fica fora de qualquer filtro de produto.
  if (f.products?.length && (o.kind === 'outro' || !f.products.includes(o.kind))) return false;
  // Pedido sem forma de pagamento registrada não entra em nenhum filtro de forma (nem no "Cartão").
  if (f.paymentMethods?.length && (!o.payment || !f.paymentMethods.includes(paymentOf(o.payment))))
    return false;
  return true;
}

/**
 * Kiwify pelo link (a 3ª régua das vendas de anúncio, a mesma no Hoje e no Tráfego): pedidos aprovados
 * do curso e do 20X com o canal de anúncio, inclusive os reembolsados depois (a regra da tela Vendas),
 * pelo valor do produto. As reembolsadas vão também à parte.
 */
export function kiwifyByLink(rows: readonly RealOrderRow[]): {
  purchases: number;
  valueCents: number;
  refunded: number;
} {
  const out = { purchases: 0, valueCents: 0, refunded: 0 };
  for (const o of rows) {
    if (!isSold(o) || o.channel !== 'meta_ads' || (o.kind !== 'front' && o.kind !== 'upsell')) continue;
    out.purchases++;
    out.valueCents += revenueOf(o);
    if (isRefundLike(o.status)) out.refunded++;
  }
  return out;
}

export interface Totals {
  /** Vendas pagas (sem as reembolsadas depois). */
  paid: number;
  /** Vendas aprovadas no período, inclusive as reembolsadas depois (as que estão em `gross`). */
  sold: number;
  /** As mesmas vendas aprovadas (`sold`) por produto: curso, 20X e consulta Premium. */
  frontSold: number;
  upsellSold: number;
  premiumSold: number;
  /**
   * Faturamento: valor dos produtos das vendas aprovadas no período, inclusive as reembolsadas depois
   * (o reembolso sai uma vez só, em `refundsGross`, pela data dele).
   */
  gross: number;
  commission: number;
  fee: number;
  interest: number;
  base: number;
  /** Valor dos produtos só dos pedidos pagos (sem os reembolsados depois): a base do ticket médio. */
  paidRevenue: number;
  /** Pedidos pagos por produto (sem os reembolsados depois): curso, 20X e consulta Premium. */
  front: number;
  upsell: number;
  premium: number;
  frontCommission: number;
  upsellCommission: number;
  frontBuyers: number;
  pending: number;
  refunds: number;
  refundsGross: number;
  refundsCommission: number;
  /** Cartão: pagos e recusados (aprovação por tentativa). */
  cardPaid: number;
  cardRefused: number;
  /** Pix: pagos, aguardando ainda no prazo e vencidos (ou trocados por outro pedido pago). */
  pixPaid: number;
  pixWaiting: number;
  pixExpired: number;
  /** Checkouts abandonados (status "abandoned" da Kiwify). */
  abandoned: number;
}

/**
 * Somas do período. `refundRows` = reembolsos feitos no período (pela data do reembolso); sem ela
 * (servidor antigo ou outra conta), os reembolsos saem de `rows`, pela data da compra.
 */
export function totals(rows: RealOrderRow[], refundRows?: RealOrderRow[] | null): Totals {
  const t: Totals = {
    paid: 0,
    sold: 0,
    frontSold: 0,
    upsellSold: 0,
    premiumSold: 0,
    gross: 0,
    commission: 0,
    fee: 0,
    interest: 0,
    base: 0,
    paidRevenue: 0,
    front: 0,
    upsell: 0,
    premium: 0,
    frontCommission: 0,
    upsellCommission: 0,
    frontBuyers: 0,
    pending: 0,
    refunds: 0,
    refundsGross: 0,
    refundsCommission: 0,
    cardPaid: 0,
    cardRefused: 0,
    pixPaid: 0,
    pixWaiting: 0,
    pixExpired: 0,
    abandoned: 0,
  };
  // Forma de pagamento só quando registrada: pedido sem ela fica fora da aprovação e do Pix.
  const pay = (o: RealOrderRow) => (o.payment ? paymentOf(o.payment) : null);
  for (const o of rows) {
    if (o.rawStatus === 'abandoned') t.abandoned++;
    const closed = isClosedWaiting(o);
    if (isPaid(o)) {
      if (pay(o) === 'pix') t.pixPaid++;
      else if (pay(o) === 'credit_card') t.cardPaid++;
    } else if (o.status === 'refused' && pay(o) === 'credit_card') t.cardRefused++;
    else if (o.status === 'waiting_payment' && pay(o) === 'pix') {
      if (closed) t.pixExpired++;
      else t.pixWaiting++;
    }
    if (isSold(o)) {
      if (o.kind === 'front') t.frontSold++;
      else if (o.kind === 'upsell') t.upsellSold++;
      else if (o.kind === 'premium') t.premiumSold++;
    }
    if (isPaid(o)) {
      t.paid++;
      t.sold++;
      t.gross += revenueOf(o);
      t.paidRevenue += revenueOf(o);
      t.commission += o.commissionCents ?? 0;
      t.fee += o.feeCents ?? 0;
      const base = o.baseCents ?? o.grossCents;
      t.base += base;
      t.interest += Math.max(0, o.grossCents - base);
      if (o.kind === 'front') {
        t.front++;
        t.frontCommission += o.commissionCents ?? 0;
      } else if (o.kind === 'upsell') {
        t.upsell++;
        t.upsellCommission += o.commissionCents ?? 0;
      } else if (o.kind === 'premium') t.premium++;
    } else if (o.status === 'waiting_payment') {
      // Pix vencido ou trocado por outro pedido pago não é dinheiro a receber.
      if (!closed) t.pending += revenueOf(o);
    } else if (isRefundLike(o.status)) {
      // Vendida no período e reembolsada depois: o faturamento do dia da venda fica (só o valor do
      // produto; juros, taxa e comissão não) e o reembolso sai pela data dele, uma vez só.
      t.sold++;
      t.gross += revenueOf(o);
      t.base += o.baseCents ?? o.grossCents;
      if (!refundRows) addRefund(t, o);
    }
  }
  // Reembolsos pela data do reembolso (lista própria do servidor); servidor antigo: pela da compra.
  for (const o of refundRows ?? []) if (isRefundLike(o.status)) addRefund(t, o);
  t.frontBuyers = t.front;
  return t;
}

function addRefund(t: Totals, o: RealOrderRow): void {
  t.refunds++;
  // No valor do produto, como o faturamento (os juros nunca entraram nele).
  t.refundsGross += revenueOf(o);
  t.refundsCommission += o.commissionCents ?? 0;
}

/**
 * Datas reais de um pedido (uma regra só para listas, jornada e atividade): criação (Pix gerado,
 * tentativa de cartão), pagamento e reembolso/chargeback. Servidor antigo: a data da venda (`at`).
 * `refundedAt` null num reembolso = data não registrada (quem precisa de um dia usa `at`).
 */
export function orderDates(o: RealOrderRow): {
  createdAt: number;
  paidAt: number | null;
  refundedAt: number | null;
} {
  const refundLike = isRefundLike(o.status);
  return {
    createdAt: o.createdAt ?? o.at,
    paidAt: o.approvedAt !== undefined ? o.approvedAt : isPaid(o) || refundLike ? o.at : null,
    refundedAt: refundLike ? (o.refundedAt ?? null) : null,
  };
}

/** Aguardando que não está mais em aberto: Pix vencido (prazo do servidor) ou trocado por outro pago. */
export const isClosedWaiting = (o: RealOrderRow): boolean =>
  o.status === 'waiting_payment' && (o.pixExpired === true || o.replaced === true);

/** Mediana de uma lista (null se vazia). */
export function median(xs: readonly number[]): number | null {
  if (!xs.length) return null;
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? (s[m] as number) : ((s[m - 1] as number) + (s[m] as number)) / 2;
}

/**
 * Minutos entre gerar o Pix e pagar, dos Pix do curso pagos (mediana). null sem Pix pago ou com o
 * servidor antigo (sem a hora de criação).
 */
export function pixMedianMinutes(rows: readonly RealOrderRow[]): number | null {
  const waits: number[] = [];
  for (const o of rows) {
    if (!isPaid(o) || o.kind !== 'front' || (o.payment ?? '').toLowerCase() !== 'pix') continue;
    if (o.createdAt == null) continue;
    const paidAt = o.approvedAt ?? o.at;
    if (paidAt >= o.createdAt) waits.push((paidAt - o.createdAt) / 60_000);
  }
  const m = median(waits);
  return m == null ? null : Math.round(m);
}

/** Valor do comprador no ato: nossa comissão do curso + do 20X, por comprador do curso. */
export const buyerValue = (t: Totals): number | null =>
  t.frontBuyers > 0 ? Math.round((t.frontCommission + t.upsellCommission) / t.frontBuyers) : null;

export function freshness(now: number, dataAsOf: number = now): Freshness[] {
  return [{ source: 'kiwify', latency: 'realtime', fetchedAt: now, dataAsOf, stale: false }];
}

export const metaFresh = (now: number, asOf: number | null): Freshness[] => [
  { source: 'meta', latency: 'near_realtime', fetchedAt: now, dataAsOf: asOf ?? now, stale: false },
];

export const rate = (a: number, b: number): number | null => (b > 0 ? a / b : null);
/** Faturamento que fica na empresa: valor dos produtos − taxa da Kiwify − reembolsos (antes da mídia). */
export const finalOf = (t: Totals): number => t.gross - t.fee - t.refundsGross;

/**
 * KPIs que são taxa (formato pct1/pct2 no `@rota/metrics`, que o app não importa): a variação sai em
 * pontos percentuais (7,8% contra 3,3% = +4,5 pp), como na simulação, e não em % relativo (+139%).
 * Um teste confere esta lista com os KpiDef.
 */
export const RATE_KPIS: ReadonlySet<KpiId> = new Set<KpiId>([
  'ctrLink',
  'connectRate',
  'connectReal',
  'hookRate',
  'holdRate',
  'oldCreativeSpendShare',
  'quizStartRate',
  'quizCompletionRate',
  'quizToVsl',
  'vslPlayRate',
  'vslEngagement',
  'pitchRetention',
  'postPitchCtr',
  'vslPageConv',
  'icRate',
  'checkoutConv',
  'cardApprovalAttempt',
  'cardApprovalBuyer',
  'pixPaidRate',
  'boletoPaidRate',
  'margin',
  'roi',
  'upsellTakeRate',
  'refundRateD7',
  'refundRateD30',
  'refundRateD60',
  'chargebackRateMonth',
  'cartAbandonRate',
  'cartRecoveryRate',
  'recoveryRate72h',
  'utmCoverage',
  'sckCoverage',
  'pixelKiwifyDivergence',
  'goalProgressRevenue',
  'goalProgressProfit',
  'goalProgressSales',
  'roiTarget',
]);

export function kv(
  id: KpiId,
  value: number | null,
  compare: number | null,
  now: number,
  spark?: (number | null)[],
  sparkCompare?: (number | null)[],
): KpiValue {
  // Taxa: diferença em pontos percentuais (mesmo com a comparação em zero); o resto, relativa.
  const pp = RATE_KPIS.has(id);
  const delta =
    value == null || compare == null
      ? null
      : pp
        ? value - compare
        : compare !== 0
          ? (value - compare) / Math.abs(compare)
          : null;
  return {
    id,
    value,
    compareValue: compare,
    delta,
    deltaKind: pp ? 'pp' : 'relative',
    status: 'neutral',
    freshness: freshness(now),
    ...(spark ? { spark } : {}),
    ...(sparkCompare ? { sparkCompare } : {}),
  };
}

/**
 * KPI sem fonte real (modo ao vivo): sem valor, sem comparação e sem frescor, com o motivo em
 * português simples na nota ("a Meta não informa isto"). Nunca o número da simulação.
 */
export function noRealKpi(id: KpiId, motivo: string): KpiValue;
export function noRealKpi(id: KpiIdV2, motivo: string): KpiValueV2;
export function noRealKpi(id: KpiId | KpiIdV2, motivo: string): KpiValue | KpiValueV2 {
  return {
    id,
    value: null,
    compareValue: null,
    delta: null,
    deltaKind: 'relative',
    status: 'neutral',
    note: motivo,
    freshness: [],
  } as KpiValue | KpiValueV2;
}

/**
 * Uma série por balde (hora num dia só; dia em períodos maiores); baldes futuros ficam nulos, e os de
 * antes do 1º pedido da base (`startAt`, início da cobertura) também: dia sem dado não é zero.
 */
export function buckets(
  rows: RealOrderRow[],
  from: number,
  to: number,
  size: number,
  now: number,
  pick: (o: RealOrderRow) => number,
  startAt?: number | null,
): { t: number[]; values: (number | null)[] } {
  const t: number[] = [];
  for (let b = bucketOf(from, size); b < to; b += size) t.push(b);
  const sums = new Map<number, number>(t.map((b) => [b, 0]));
  for (const o of rows) {
    const b = bucketOf(o.at, size);
    if (sums.has(b)) sums.set(b, (sums.get(b) ?? 0) + pick(o));
  }
  const start = startAt ?? Number.NEGATIVE_INFINITY;
  return { t, values: t.map((b) => (b > now || b + size <= start ? null : (sums.get(b) ?? 0))) };
}

export const cumulative = (v: (number | null)[]): (number | null)[] => {
  let acc = 0;
  return v.map((x) => {
    if (x == null) return null;
    acc += x;
    return acc;
  });
};

/**
 * Motivo da recusa em português simples. Código conhecido da Kiwify/adquirente vira frase; o resto
 * aparece como veio (nada escondido).
 */
const REFUSAL_TEXT: Record<string, string> = {
  insufficient_funds: 'Saldo insuficiente',
  not_enough_balance: 'Saldo insuficiente',
  limit_exceeded: 'Limite do cartão',
  card_declined: 'Cartão recusado pelo banco',
  generic_decline: 'Cartão recusado pelo banco',
  do_not_honor: 'Cartão recusado pelo banco',
  declined: 'Cartão recusado pelo banco',
  expired_card: 'Cartão vencido',
  invalid_cvv: 'Código de segurança errado',
  incorrect_cvc: 'Código de segurança errado',
  invalid_card_number: 'Número do cartão errado',
  incorrect_number: 'Número do cartão errado',
  invalid_card: 'Dados do cartão errados',
  card_blocked: 'Cartão bloqueado',
  blocked_card: 'Cartão bloqueado',
  lost_card: 'Cartão bloqueado',
  stolen_card: 'Cartão bloqueado',
  fraud: 'Suspeita de fraude',
  suspected_fraud: 'Suspeita de fraude',
  antifraud: 'Barrado pelo antifraude',
  high_risk: 'Barrado pelo antifraude',
  transaction_not_allowed: 'Compra não permitida no cartão',
  processing_error: 'Erro no processamento',
};

export function refusalText(reason: string | null | undefined): string | null {
  const r = reason?.trim();
  if (!r) return null;
  return REFUSAL_TEXT[r.toLowerCase().replace(/[\s-]+/g, '_')] ?? r;
}

export function paymentOf(p: string | null): PaymentMethod {
  const s = (p ?? '').toLowerCase();
  if (s === 'pix') return 'pix';
  if (s.includes('boleto')) return 'boleto';
  return 'credit_card';
}

/**
 * Status do pedido no painel. Carrinho abandonado (status "other" com o status da Kiwify "abandoned")
 * vira 'abandoned': abre no detalhe ("Abriu o checkout e saiu"), mas não entra na lista de pedidos.
 */
export function statusOf(s: string, raw?: string | null): OrderStatus | 'abandoned' | null {
  if (s === 'paid' || s === 'refused' || s === 'refunded' || s === 'waiting_payment') return s;
  if (s === 'chargeback' || s === 'chargedback') return 'chargedback';
  if (raw === 'abandoned' || s === 'abandoned') return 'abandoned';
  return null;
}

export const channelV2 = {
  automacao: 'ig_dm',
  ig_bio: 'ig_bio',
  meta_ads: 'meta_ads',
  outro: 'ig_outro',
  sem_origem: 'sem_origem',
} as const;

/** Linha vazia, para reaproveitar a soma por balde com outros valores (ex.: gasto por hora). */
export const row0: RealOrderRow = {
  id: '',
  ref: null,
  at: 0,
  status: 'paid',
  product: null,
  kind: 'outro',
  grossCents: 0,
  baseCents: null,
  commissionCents: null,
  feeCents: null,
  installments: null,
  payment: null,
  channel: 'sem_origem',
  utmSource: null,
  utmContent: null,
  firstTouch: null,
  lastTouch: null,
  conversation: false,
};

/**
 * Tipo do produto no pedido do painel: curso, 20X ou Premium (consulta individual, nunca o 20X).
 * Produto que o servidor não reconhece ("outro") não vira pedido do painel (não há etiqueta para ele, e
 * virar "20X" seria mentira): fica fora das listas, do stream e dos filtros de produto. As contagens por
 * produto usam o `kind` do servidor, então ele também não entra no curso nem no 20X.
 */
/** Pedido do modo ao vivo: o do contrato com o que só existe aqui (forma não informada, automação). */
export type LiveOrder = Order & {
  channel?: string;
  campaignName?: string | null;
  adName?: string | null;
  /** A Kiwify não registrou a forma de pagamento (o `paymentMethod` é só um valor de reserva). */
  paymentUnknown?: true;
  /** Venda da automação do Instagram (marcador do contato ou link da automação). */
  automation?: 'contact' | 'link';
  /**
   * Carrinho abandonado: abriu o checkout e saiu sem gerar pagamento. O `status` fica 'waiting_payment'
   * só porque o contrato não tem esse estado; quem mostra o pedido usa esta marca (e as listas o deixam
   * de fora).
   */
  abandoned?: true;
};

/** UTM com ID da Meta ({{campaign.id}}, {{ad.id}}): só dígitos, 10 ou mais. */
const META_ID = /^\d{10,}$/;

/** Método de atribuição pela atribuição gravada no pedido e pelas UTMs (nunca pela conversa). */
function attributionOf(o: RealOrderRow): Order['attribution'] {
  const utms = [o.utmSource, o.utmMedium, o.utmCampaign, o.utmContent, o.utmTerm];
  if (!utms.some(Boolean)) return { method: 'unattributed', reason: 'utm_empty' };
  if ([o.utmCampaign, o.utmContent].some((v) => v && META_ID.test(v))) return { method: 'utm_ids' };
  return { method: 'utm_names' };
}

export function toOrder(o: RealOrderRow): LiveOrder | null {
  const st = statusOf(o.status, o.rawStatus);
  if (!st || o.kind === 'outro') return null;
  const abandoned = st === 'abandoned';
  const status: OrderStatus = abandoned ? 'waiting_payment' : st;
  const base = o.baseCents ?? o.grossCents;
  const dates = orderDates(o);
  const tracking: Order['tracking'] = {};
  const raw: [keyof Order['tracking'], string | null | undefined][] = [
    ['utm_source', o.utmSource],
    ['utm_medium', o.utmMedium],
    ['utm_campaign', o.utmCampaign],
    ['utm_content', o.utmContent],
    ['utm_term', o.utmTerm],
    // Servidor antigo só mandava o "último toque" do src.
    ['src', o.src !== undefined ? o.src : o.lastTouch ? `ultimo_${o.lastTouch}` : null],
    ['sck', o.sck],
  ];
  for (const [k, v] of raw) if (v) tracking[k] = v;
  const order: LiveOrder = {
    id: o.id,
    ref: o.ref ?? o.id.slice(0, 8),
    product: o.kind,
    productName: o.product ?? '',
    status,
    paymentMethod: paymentOf(o.payment),
    installments: o.installments,
    cardRejectionReason: status === 'refused' ? refusalText(o.refusalReason) : null,
    // Faturamento = valor do produto (os juros do parcelamento ficam com a Kiwify e aparecem à parte);
    // cobrado = o que o comprador pagou; líquido = nossa comissão; coprodução só como informação.
    amounts: {
      basePrice: base,
      discountPct: 0,
      gross: base,
      charged: o.grossCents,
      interest: Math.max(0, o.grossCents - base),
      fee: o.feeCents ?? 0,
      net: o.commissionCents ?? 0,
      ...(o.coproductionCents != null ? { coproduction: o.coproductionCents } : {}),
    },
    parentOrderId: null,
    refundRequestedAt: null,
    customer: null,
    tracking,
    attribution: attributionOf(o),
    sessionId: null,
    // Datas reais do servidor (criação, aprovação, reembolso); servidor antigo: a data da venda.
    createdAt: dates.createdAt,
    approvedAt: dates.paidAt,
    refundedAt: dates.refundedAt,
    pixExpiresAt: abandoned
      ? null
      : status === 'waiting_payment' && o.replaced
        ? Math.min(o.pixExpiresAt ?? o.at, o.at)
        : (o.pixExpiresAt ?? null),
    channel: channelV2[o.channel],
    campaignName: o.campaignName ?? null,
    adName: o.adName ?? null,
    ...(o.buyer ? { buyerKey: o.buyer } : {}),
    ...(o.payment ? {} : { paymentUnknown: true as const }),
    ...(abandoned ? { abandoned: true as const } : {}),
    ...(o.attribution === 'contact'
      ? { automation: 'contact' as const }
      : o.attribution === 'automation'
        ? { automation: 'link' as const }
        : {}),
  };
  return order;
}

/** Janela para ligar o 20X ao pedido do curso da mesma pessoa (o upsell sai logo depois do curso). */
export const UPSELL_PARENT_MS = 2 * HOUR;

/**
 * Liga cada 20X ao pedido do curso APROVADO da mesma pessoa (`buyerKey`), o mais recente antes dele,
 * até 2 h antes: a quebra do VEN-04 usa a forma de pagamento e a origem do curso. Sem o curso na
 * lista (ou sem o comprador), o 20X fica com os próprios dados. Muda os pedidos recebidos.
 */
export function linkUpsellParents(orders: Order[]): void {
  const fronts = new Map<string, Order[]>();
  for (const o of orders) {
    if (o.product !== 'front' || !o.buyerKey || o.approvedAt == null || o.status === 'refused') continue;
    const list = fronts.get(o.buyerKey) ?? [];
    list.push(o);
    fronts.set(o.buyerKey, list);
  }
  for (const o of orders) {
    if (o.product !== 'upsell' || !o.buyerKey || o.parentOrderId) continue;
    const at = o.approvedAt ?? o.createdAt;
    let best: Order | null = null;
    for (const f of fronts.get(o.buyerKey) ?? []) {
      const fa = f.approvedAt ?? 0;
      if (fa > at || at - fa > UPSELL_PARENT_MS) continue;
      if (!best || fa > (best.approvedAt ?? 0)) best = f;
    }
    if (best) o.parentOrderId = best.id;
  }
}

/** Pedido aberto agora (Pix no prazo, boleto de até 3 dias), com a regra da aba Pendentes. */
export const isOpenRow = (o: RealOrderRow, at: number): boolean => {
  const ord = toOrder(o);
  return ord != null && isOpenNow(ord, at);
};

export function paginate<T>(rows: T[], q: ListQuery): Page<T> {
  const offset = q.cursor ? Number.parseInt(q.cursor, 36) || 0 : 0;
  const limit = Math.max(1, Math.min(200, q.limit));
  const page = rows.slice(offset, offset + limit);
  const next = offset + limit < rows.length ? (offset + limit).toString(36) : null;
  return { rows: page, nextCursor: next, total: rows.length };
}

/**
 * Silêncio da Kiwify que vira aviso, pelo ritmo dos pedidos que o painel tem (servidor antigo, sem o
 * `silenceWarnMs`): a mesma regra do servidor (`kiwifySilenceLimits`), com os pedidos dos últimos 7
 * dias (quem chama lê esses dias). Na faixa de agora (madrugada 0h–8h ou dia), o maior intervalo entre
 * dois pedidos; aviso com 1,5× isso, entre 1 h e 6 h. Com pouco histórico (menos de 20 pedidos ou de
 * 1 dia), 3 h de dia e 6 h de madrugada.
 */
export function silenceWarnFrom(times: readonly number[], now: number): number {
  const night = (t: number) => new Date(t - BRT).getUTCHours() < 8;
  const ats = times.filter((t) => t > now - 7 * DAY && t <= now).sort((a, b) => a - b);
  const first = ats[0];
  if (ats.length < 20 || first == null || now - first < DAY) return night(now) ? 6 * HOUR : 3 * HOUR;
  // Como no servidor: cada intervalo conta, em cada faixa, o tamanho que tinha no último instante dele
  // dentro dela (a pausa da noite até as 9h conta até as 8h para a madrugada e 1 h para o dia).
  let maxNight = 0;
  let maxDay = 0;
  for (let i = 1; i < ats.length; i++) {
    const a = ats[i - 1] as number;
    const b = ats[i] as number;
    const start = Math.floor((b - BRT) / DAY) * DAY + BRT; // 0h em SP do dia do fim
    if (night(b)) {
      maxNight = Math.max(maxNight, b - a);
      if (start > a) maxDay = Math.max(maxDay, start - a);
    } else {
      maxDay = Math.max(maxDay, b - a);
      if (start + 8 * HOUR > a) maxNight = Math.max(maxNight, start + 8 * HOUR - a);
    }
  }
  return Math.min(6 * HOUR, Math.max(HOUR, 1.5 * (night(now) ? maxNight : maxDay)));
}

/** Situação das leituras de pedidos: falhas seguidas, a última mensagem e a última leitura boa. */
export interface OrdersHealth {
  /** Consultas seguidas que falharam (zera na primeira que dá certo). */
  fails: number;
  lastError: string | null;
  lastFailAt: number | null;
  lastOkAt: number | null;
}

const errorMessage = (e: unknown): string =>
  e && typeof e === 'object' && 'message' in e && typeof e.message === 'string' && e.message
    ? e.message
    : 'erro no servidor';

/**
 * Recorte de uma leitura maior com a regra do servidor (`painelSales.ts`): pedido no período pela data
 * da venda (`at`), reembolso pela data do reembolso, sempre `[from, to)`.
 */
export function sliceOrders(w: RealOrdersResult, from: number, to: number): RealOrdersResult {
  const inside = (t: number | null | undefined) => t != null && t >= from && t < to;
  const { refunds, ...rest } = w;
  return {
    ...rest,
    orders: w.orders.filter((o) => inside(o.at)),
    ...(refunds ? { refunds: refunds.filter((o) => inside(o.refundedAt ?? o.at)) } : {}),
  };
}

/**
 * Pedidos com cache curto por intervalo; guarda o último aviso da Kiwify visto e o início do webhook.
 * Se uma leitura falha, devolve a última leitura boa do mesmo começo (o Hoje de 1 min atrás, por
 * exemplo) marcada com `staleSince`: os números reais ficam na tela, parados, em vez de sumir ou de
 * voltar para a simulação. Sem leitura boa, a falha segue (a tela mostra o erro com "Tentar de novo").
 *
 * Janela larga (`wideDays`): o Hoje abre pedindo uns 15 períodos (hoje, ontem até agora, 7D, 30D,
 * desde julho...), e cada consulta traz os pedidos inteiros do período. Elas iam todas ao servidor,
 * que monta uma de cada vez: a tela levava ~10 s. Agora, todo período que cabe nos últimos `wideDays`
 * dias até o minuto de agora sai de uma consulta só dessa janela, recortada aqui com a mesma regra do
 * servidor (pedido pela data da venda, reembolso pela data do reembolso, `[from, to)`). Se a janela
 * falha ou vem incompleta, o período vai ao servidor como antes.
 */
export function createOrdersSource(fetchOrders: FetchOrders, clock: () => number, wideDays = 100) {
  const cache = new Map<string, { at: number; p: Promise<RealOrdersResult> }>();
  /** Última leitura boa por começo do intervalo (poucos começos: hoje, ontem, 7D, 30D...). */
  const lastGood = new Map<number, { to: number; at: number; r: RealOrdersResult }>();
  let lastEventAt: number | null = null;
  let webhookSince: number | null = null;
  let dataStart: number | null = null;
  let silenceWarnMs: number | null = null;
  const health: OrdersHealth = { fails: 0, lastError: null, lastFailAt: null, lastOkAt: null };
  /** Consultas que foram ao servidor (as do `cache` de cima podem ser recortes da janela larga). */
  const raw = new Map<string, { at: number; p: Promise<RealOrdersResult> }>();
  const loadFull = (from: number, to: number): Promise<RealOrdersResult> => {
    const key = `${from}|${to}`;
    const hit = cache.get(key);
    if (hit && clock() - hit.at < CACHE_MS) return hit.p;
    const now = clock();
    const wideTo = Math.ceil(now / 60_000) * 60_000;
    const wideFrom = dayStart(todayKey(now)) - wideDays * DAY;
    if (wideDays <= 0 || from < wideFrom || to > wideTo || (from === wideFrom && to === wideTo))
      return loadRaw(from, to);
    // Sem a janela, o período vai sozinho ao servidor; se também falhar, serve o recorte da última
    // janela boa (parado desde a leitura dela), como a leitura boa do mesmo começo faz.
    const alone = () =>
      loadRaw(from, to).catch((e: unknown) => {
        const prev = lastGood.get(wideFrom);
        if (prev && !prev.r.incomplete && prev.to > from)
          return { ...sliceOrders(prev.r, from, Math.min(to, prev.to)), staleSince: prev.at };
        throw e;
      });
    const p = loadRaw(wideFrom, wideTo).then(
      (w) => (w.incomplete || w.staleSince != null ? alone() : sliceOrders(w, from, to)),
      alone,
    );
    cache.set(key, { at: now, p });
    p.then(
      (r) => {
        if (r.staleSince != null) cache.delete(key);
      },
      () => cache.delete(key),
    );
    return p;
  };
  const loadRaw = (from: number, to: number): Promise<RealOrdersResult> => {
    const key = `${from}|${to}`;
    const hit = raw.get(key);
    if (hit && clock() - hit.at < CACHE_MS) return hit.p;
    const p = fetchOrders(from, to).then(
      (r) => {
        if (r.lastEventAt != null) lastEventAt = Math.max(lastEventAt ?? 0, r.lastEventAt);
        if (r.webhookSince != null) webhookSince = Math.min(webhookSince ?? r.webhookSince, r.webhookSince);
        if (r.dataStartMs != null) dataStart = Math.min(dataStart ?? r.dataStartMs, r.dataStartMs);
        if (r.silenceWarnMs != null) silenceWarnMs = r.silenceWarnMs;
        const now = clock();
        health.fails = 0;
        health.lastOkAt = now;
        const prev = lastGood.get(from);
        if (!prev || to >= prev.to) {
          lastGood.delete(from);
          lastGood.set(from, { to, at: now, r });
          if (lastGood.size > 24) lastGood.delete(lastGood.keys().next().value as number);
        }
        return r;
      },
      (e: unknown) => {
        health.fails += 1;
        health.lastError = errorMessage(e);
        health.lastFailAt = clock();
        // Só serve a leitura boa que cabe no pedido (mesmo começo, até antes do fim pedido).
        const prev = lastGood.get(from);
        if (prev && prev.to <= to) return { ...prev.r, staleSince: prev.at };
        throw e;
      },
    );
    raw.set(key, { at: clock(), p });
    // Falha (mesmo servida pela última leitura boa) não fica no cache: a próxima tela tenta de novo.
    p.then(
      (r) => {
        if (r.staleSince != null) raw.delete(key);
      },
      () => raw.delete(key),
    );
    return p;
  };
  return {
    load: (from: number, to: number) => loadFull(from, to).then((r) => r.orders),
    /** Reembolsos feitos no período (null = servidor antigo, sem a lista: conte pela data da compra). */
    loadRefunds: (from: number, to: number) => loadFull(from, to).then((r) => r.refunds ?? null),
    loadFull,
    /**
     * Começa a ler a janela larga já (sem esperar ninguém pedir): no boot ao vivo, a simulação refaz o
     * dia com os ajustes salvos (~2 s no worker) antes do Hoje pedir o snapshot; a consulta corre nesse
     * tempo e o snapshot já acha os pedidos no cache. Sem janela larga, não faz nada.
     */
    prefetch: () => {
      if (wideDays <= 0) return;
      const now = clock();
      void loadFull(dayStart(todayKey(now)), Math.ceil(now / 60_000) * 60_000).catch(() => undefined);
    },
    invalidate: () => {
      cache.clear();
      raw.clear();
    },
    lastEventAt: () => lastEventAt,
    webhookSince: () => webhookSince,
    /** Início (dia, SP) do 1º pedido da base, visto em qualquer consulta; null = ainda não se sabe. */
    dataStart: () => coverageStart(dataStart),
    /** Limite de silêncio da Kiwify que o servidor mandou (null = servidor antigo ou ainda sem leitura). */
    silenceWarnMs: () => silenceWarnMs,
    /** Falhas seguidas das leituras de pedidos (o rodapé vira "com erro" quando elas se repetem). */
    health: (): Readonly<OrdersHealth> => ({ ...health }),
  };
}

// ---------- contrato dos módulos ----------

/** O que o núcleo calcula com os pedidos do período (`realKpis`) e entrega aos módulos. */
export interface RealKpisResult {
  kpis: Partial<Record<KpiId, KpiValue>>;
  t: Totals;
  c: Totals | null;
  media: RealMedia | null;
  /** Por que `media` é null (erro ou não conectada); ausente quando não se sabe. */
  mediaGap?: MediaGap | null;
  /** Os pedidos vieram da última leitura boa (a consulta falhou): desde quando estão parados. */
  ordersStaleSince?: number | null;
  /**
   * A leitura dos pedidos falhou e não havia leitura boa antes (1ª carga, período ou comparação
   * novos): os números de vendas saem sem valor; `t`/`c` são zeros de enchimento e não valem.
   */
  ordersDown?: true;
}

/** Entrada do `kpis()` de um módulo: o período, a comparação e os totais de vendas já somados. */
export interface RealKpiInput {
  q: Query;
  now: number;
  r: { from: number; to: number; end: number };
  cr: { from: number; to: number; end?: number } | null;
  /** Balde dos minigráficos (hora ou dia). */
  size: number;
  t: Totals;
  c: Totals | null;
}

/** Contexto que o `withRealSales` monta e passa a cada módulo (fontes reais com cache e relógio). */
export interface RealCtx {
  /** Provider da simulação, por baixo. */
  provider: DataProvider;
  v2: PainelProviderV2;
  clock(): number;
  load(from: number, to: number): Promise<RealOrderRow[]>;
  /** Reembolsos feitos no período, pela data do reembolso (null = servidor antigo). Opcional. */
  loadRefunds?(from: number, to: number): Promise<RealOrderRow[] | null>;
  /** 1º aviso do webhook da Kiwify (antes disso, só a exportação). Opcional. */
  webhookSince?(): number | null;
  /** Início (dia, SP) do 1º pedido da base: antes disso não há dado (null), nunca zero. Opcional. */
  dataStart?(): number | null;
  invalidateOrders(): void;
  /** Último aviso da Kiwify recebido (qualquer pedido). */
  lastEventAt(): number | null;
  loadMedia(from: number, to: number): Promise<RealMedia | null>;
  loadInsights(from: number, to: number, level: 'campaign' | 'adset' | 'ad'): Promise<RealInsights | null>;
  /** Limpa o cache da Meta (sincronização nova). */
  invalidateMedia(): void;
  loadVsl(from: number, to: number): Promise<RealVsl | null>;
  lastVsl(): RealVsl | null;
  /** Custo real da mídia (com imposto ou câmbio e IOF). */
  costOf(media: RealMedia): number;
  /** Imposto da Meta sobre a mídia (Ajustes › Custos). */
  metaTaxPct(): number;
  /** Ajustes salvos (null antes do boot). */
  settings(): Settings | null;
  realKpis(q: Query): Promise<RealKpisResult>;
  metaFresh(now: number, asOf: number | null): Freshness[];
  kiwiFresh(now: number): Freshness[];
}

export type RealEmit = (m: { type: LiveMessage['type']; topic: LiveMessage['topic']; data: unknown }) => void;

/** Página de uma lista (`list(kind)`), como o provider devolve. */
export type AnyPage = Page<unknown>;

/**
 * Um assunto do modo ao vivo. Toda função é opcional; devolver `undefined` = "não é comigo", segue o
 * núcleo (e, por fim, a simulação).
 */
export interface RealModule {
  /** Métodos do provider que este módulo troca inteiros. */
  overrides?: Partial<DataProvider & PainelProviderV2>;
  /** KPIs reais que entram no `realKpis` (snapshot, detalhe da métrica e `kpi.patch` do stream). */
  kpis?(input: RealKpiInput): Promise<Partial<Record<KpiId, KpiValue>>>;
  /** IDs que o `kpis()` pode devolver: o detalhe da métrica só pergunta ao `realKpis` por estes. */
  kpiIds?: readonly KpiId[];
  /** KPIs que nunca vêm da simulação no stream (saem do `kpi.patch` simulado mesmo sem valor real). */
  neverSimKpis?: readonly KpiId[];
  /** Ajusta o snapshot montado pelo núcleo (roda depois dele, em paralelo com os outros módulos). */
  patchSnapshot?(q: Query, snap: DashboardSnapshot, real: RealKpisResult): void | Promise<void>;
  /** Ajusta os KPIs da V2 (roda depois do núcleo, em paralelo com os outros módulos). */
  patchKpisV2?(
    q: Parameters<NonNullable<PainelProviderV2['getKpisV2']>>[0],
    out: Partial<Record<KpiIdV2, KpiValueV2>>,
    sim: Partial<Record<KpiIdV2, KpiValueV2>>,
  ): void | Promise<void>;
  series?(q: Parameters<DataProvider['getSeries']>[0]): Promise<ColumnarSeries | undefined>;
  list?(kind: ListKind, q: ListQuery): Promise<AnyPage | undefined> | undefined;
  kpiDetail?(
    q: Parameters<DataProvider['getKpiDetail']>[0],
  ): Promise<Awaited<ReturnType<DataProvider['getKpiDetail']>> | undefined>;
  /** Ajusta o boot (`init` e `switchViewer`). */
  patchBoot?<B extends { settings: Settings }>(boot: B): Promise<B>;
  /** Sincronização nova da Meta: limpar o que depende dela. */
  onMediaSync?(): void;
  /** Liga junto com o stream; devolve como desligar. */
  subscribe?(emit: RealEmit): () => void;
}

/**
 * Grava um KPI da V2 real em `out` (só se foi pedido), com estado neutro: sem os limites do
 * `@rota/metrics` no app, nada de verde/vermelho inventado.
 */
export function setKpiV2(
  q: { ids: readonly KpiIdV2[] },
  out: Partial<Record<KpiIdV2, KpiValueV2>>,
  sim: Partial<Record<KpiIdV2, KpiValueV2>>,
  id: KpiIdV2,
  value: number | null,
  fresh: Freshness[],
  extra: Partial<KpiValueV2> = {},
): void {
  if (!q.ids.includes(id)) return;
  out[id] = {
    ...(sim[id] ?? { deltaKind: 'relative' }),
    id,
    value,
    compareValue: null,
    delta: null,
    status: 'neutral',
    freshness: fresh as KpiValueV2['freshness'],
    ...extra,
  } as KpiValueV2;
  if (!extra.note) delete (out[id] as { note?: string }).note;
}
